import { useEffect, type ReactNode } from 'react';

export interface FramesZoomItem {
  url: string;
  caption: string;
}

export interface FramesZoomModalProps {
  items: FramesZoomItem[];
  index: number;
  onChange: (nextIndex: number) => void;
  onClose: () => void;
  hint?: ReactNode;
}

/**
 * Lightweight image zoom modal with optional prev/next navigation across a
 * sequence of related frames. Used both by the Phase 6 test evidence pane
 * and the Extras "Gameplay Video" per-frame list so the popup behaves the
 * same in both places (keyboard arrows, Esc to close, backdrop click closes).
 *
 * Backdrop click closes the dialog; clicks inside the white modal frame
 * do not (matches the existing image-zoom CSS contract).
 */
export function FramesZoomModal({
  items,
  index,
  onChange,
  onClose,
  hint,
}: FramesZoomModalProps) {
  const current = items[index];
  const total = items.length;
  const hasSequence = total > 1;
  const hasPrev = hasSequence && index > 0;
  const hasNext = hasSequence && index < total - 1;

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (!hasSequence) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (index < total - 1) onChange(index + 1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (index > 0) onChange(index - 1);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [hasSequence, index, total, onChange, onClose]);

  if (!current) return null;

  const goto = (delta: number) => {
    const next = Math.min(total - 1, Math.max(0, index + delta));
    if (next !== index) onChange(next);
  };

  return (
    <div className="modal-backdrop image-zoom-backdrop" onClick={onClose}>
      <div
        className="image-zoom"
        onClick={(e) => e.stopPropagation()}
        style={{ position: 'relative' }}
      >
        <div className="row" style={{ marginBottom: 8 }}>
          <strong>{current.caption}</strong>
          {hasSequence && (
            <span className="muted small" style={{ marginLeft: 8 }}>
              {index + 1} / {total}
            </span>
          )}
          <span className="spacer" />
          <button className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
        </div>
        <div style={{ position: 'relative' }}>
          <img src={current.url} alt={current.caption} />
          {hasSequence && (
            <>
              <button
                type="button"
                aria-label="Previous frame"
                onClick={(e) => {
                  e.stopPropagation();
                  goto(-1);
                }}
                disabled={!hasPrev}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: 8,
                  transform: 'translateY(-50%)',
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  border: 'none',
                  background: 'rgba(0,0,0,0.55)',
                  color: '#fff',
                  fontSize: 20,
                  cursor: hasPrev ? 'pointer' : 'not-allowed',
                  opacity: hasPrev ? 1 : 0.35,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {'\u2039'}
              </button>
              <button
                type="button"
                aria-label="Next frame"
                onClick={(e) => {
                  e.stopPropagation();
                  goto(1);
                }}
                disabled={!hasNext}
                style={{
                  position: 'absolute',
                  top: '50%',
                  right: 8,
                  transform: 'translateY(-50%)',
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  border: 'none',
                  background: 'rgba(0,0,0,0.55)',
                  color: '#fff',
                  fontSize: 20,
                  cursor: hasNext ? 'pointer' : 'not-allowed',
                  opacity: hasNext ? 1 : 0.35,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {'\u203A'}
              </button>
            </>
          )}
        </div>
        {hasSequence && (
          <p className="muted small" style={{ marginTop: 8, marginBottom: 0 }}>
            {hint ?? (
              <>
                Use the arrows or <kbd>{'\u2190'}</kbd> / <kbd>{'\u2192'}</kbd>{' '}
                keys to switch frames. <kbd>Esc</kbd> to close.
              </>
            )}
          </p>
        )}
      </div>
    </div>
  );
}
